import React, { useState } from 'react';
import { FOLLOW_PACKS, type FollowPack } from '@/lib/followPacks';
import { useBulkFollow } from '@/hooks/useBulkFollow';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Check, Users, Sparkles } from 'lucide-react';

interface FollowPacksProps {
  onComplete?: () => void;
}

export function FollowPacks({ onComplete }: FollowPacksProps) {
  const { user } = useCurrentUser();
  const bulkFollow = useBulkFollow();
  const [selectedPack, setSelectedPack] = useState<string | null>(null);

  const handleFollowPack = async (pack: FollowPack) => {
    setSelectedPack(pack.id);
    const pubkeys = pack.accounts.map(a => a.pubkey);
    await bulkFollow.mutateAsync(pubkeys);
    setSelectedPack(null);
    onComplete?.();
  };

  if (!user) return null;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Sparkles className="size-5 text-sky-500" />
        <span className="font-extrabold text-[15px]">Discover People to Follow</span>
      </div>
      <p className="text-xs text-muted-foreground leading-normal">
        Pick a category and follow a curated group of Nostr accounts in one click.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
        {FOLLOW_PACKS.map((pack) => (
          <Card key={pack.id} className="rounded-2xl border border-border/80 shadow-none bg-secondary/20 hover:bg-secondary/30 transition-colors">
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{pack.icon}</span>
                  <CardTitle className="text-sm font-extrabold">{pack.name}</CardTitle>
                </div>
                <Button
                  type="button"
                  disabled={bulkFollow.isPending && selectedPack === pack.id}
                  onClick={() => handleFollowPack(pack)}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold h-8 px-4 text-xs cursor-pointer"
                >
                  {bulkFollow.isPending && selectedPack === pack.id ? (
                    <Loader2 className="size-3.5 animate-spin mr-1" />
                  ) : (
                    <Users className="size-3.5 mr-1" />
                  )}
                  Follow All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              <p className="text-[10px] text-muted-foreground mb-2">{pack.description}</p>
              <div className="flex flex-wrap gap-1">
                {pack.accounts.map((account) => (
                  <span
                    key={account.pubkey}
                    className="text-[10px] font-mono text-muted-foreground bg-secondary/40 px-2 py-0.5 rounded-full border border-border/30"
                  >
                    @{account.pubkey.slice(0, 6)}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}